import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { X } from 'lucide-react'
import { STATUS_PUNKT, statusLabel, statusTon } from './StatusBadge'

/** useState mit Ablage in sessionStorage (Filter/Suche/Sortierung bleiben beim Seitenwechsel erhalten). Leerer Key = nur State. */
export function usePersistState<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [wert, setWert] = useState<T>(() => {
    if (!key) return initial
    try { const s = sessionStorage.getItem('admin.liste.' + key); return s != null ? JSON.parse(s) as T : initial } catch { return initial }
  })
  const setze = useCallback((v: T | ((p: T) => T)) => {
    setWert(prev => {
      const neu = typeof v === 'function' ? (v as (p: T) => T)(prev) : v
      if (key) { try { sessionStorage.setItem('admin.liste.' + key, JSON.stringify(neu)) } catch { /* ignorieren */ } }
      return neu
    })
  }, [key])
  return [wert, setze]
}

/** „Filter zurücksetzen“, nur sichtbar wenn ein Filter aktiv ist. */
export function FilterZurueck({ aktiv, onClick }: { aktiv: boolean; onClick: () => void }) {
  if (!aktiv) return null
  return (
    <button type="button" onClick={onClick}
      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-sm text-primary-400 hover:bg-primary-400/10 hover:text-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400">
      <X className="h-4 w-4" />Filter zurücksetzen
    </button>
  )
}

export interface Kennzahl {
  label: string
  anzahl: number
  aktiv: boolean
  onClick: () => void
  punkt?: string // Tailwind-bg-Klasse
}

/** Klickbare Kennzahlen-Chips; Klick setzt den passenden Filter (erneuter Klick auf aktive Kennzahl = alle). */
export function KennzahlenLeiste({ items }: { items: Kennzahl[] }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Kennzahlen und Schnellfilter">
      {items.map(k => (
        <button key={k.label} type="button" onClick={k.onClick} aria-pressed={k.aktiv}
          className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-3.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
            k.aktiv ? 'border-primary-500/60 bg-primary-500/15 text-dark-text' : 'border-dark-border bg-dark-bg-tertiary/30 text-dark-text-muted hover:bg-dark-bg-tertiary/60 hover:text-dark-text'}`}>
          {k.punkt && <span aria-hidden="true" className={`h-2 w-2 rounded-full ${k.punkt}`} />}
          <span className="font-semibold tabular-nums text-dark-text">{k.anzahl}</span>
          <span>{k.label}</span>
        </button>
      ))}
    </div>
  )
}

/** Kennzahlen „Alle“ + je Status-Rohwert (Anzahl, Farbpunkt); Klick setzt den Statusfilter. */
export function statusKennzahlen(werte: (string | null | undefined)[], aktuell: string, setze: (v: string) => void, alleLabel = 'Alle'): Kennzahl[] {
  const z = new Map<string, number>()
  werte.forEach(w => { if (w) z.set(w, (z.get(w) ?? 0) + 1) })
  return [
    { label: alleLabel, anzahl: werte.length, aktiv: aktuell === 'all', onClick: () => setze('all') },
    ...Array.from(z.entries()).sort((a, b) => statusLabel(a[0]).localeCompare(statusLabel(b[0]), 'de')).map(([w, n]) => ({
      label: statusLabel(w), anzahl: n, aktiv: aktuell === w, punkt: STATUS_PUNKT[statusTon(w)],
      onClick: () => setze(aktuell === w ? 'all' : w),
    })),
  ]
}

/** `?neu=1` (z. B. aus der Befehlspalette) öffnet das Anlegen-Formular der Seite einmalig. */
export function useNeuParam(oeffneNeu: () => void, bereit: boolean) {
  const [p, setP] = useSearchParams()
  useEffect(() => {
    if (!bereit || p.get('neu') !== '1') return
    setP(prev => { const n = new URLSearchParams(prev); n.delete('neu'); return n }, { replace: true })
    oeffneNeu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p, bereit])
}
